import { type ReactNode, useEffect, useState } from 'react';
import i18n from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Supported languages
export const supportedLanguages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
] as const;

export type LanguageCode = (typeof supportedLanguages)[number]['code'];

// Initialize i18next
const i18nInstance = i18n.createInstance();

i18nInstance
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'de',
    supportedLngs: supportedLanguages.map((l) => l.code),
    defaultNS: 'common',
    ns: [
      'common',
      'employees',
      'visitors',
      'visitor-registration',
      'reception',
      'admin',
      'auth',
      'navigation',
      'kiosk',
      'search',
      'incidents',
      'contractors',
      'contacts',
      'files',
      'notifications',
      'rooms',
      'issuance',
      'workflow',
      'outlook',
      'security',
      'trackingLog',
      'approvals',
    ],
    debug: false, //import.meta.env.DEV,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'maja-language',
      caches: ['localStorage'],
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    react: {
      useSuspense: false, // Avoid Suspense issues with lazy loading
    },
  });

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [isReady, setIsReady] = useState(i18nInstance.isInitialized);

  useEffect(() => {
    if (i18nInstance.isInitialized) {
      setIsReady(true);
      return;
    }

    const handleInitialized = () => {
      setIsReady(true);
    };

    i18nInstance.on('initialized', handleInitialized);
    return () => {
      i18nInstance.off('initialized', handleInitialized);
    };
  }, []);

  if (!isReady) {
    return null; // Or a loading spinner
  }

  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>;
}

// Helper hook for language switching
export function useLanguage() {
  const changeLanguage = (code: LanguageCode) => {
    i18nInstance.changeLanguage(code);
    localStorage.setItem('maja-language', code);
  };

  const currentLanguage = supportedLanguages.find((l) => l.code === i18nInstance.language) ?? supportedLanguages[0];

  return {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
  };
}

export { i18nInstance as i18n };
